/** @jsxImportSource ../../i18n */
import React, { useEffect, useState } from 'react';
import { ExternalLink, Landmark } from 'lucide-react';
import api from '../../services/api';
import { formatDate, formatNumber, formatPercent } from '../../theme/format';
import { Badge, Card, CardHeader, Money } from '../ui';

const KIND = {
  buy: ['gain', 'Achat'],
  sell: ['loss', 'Vente'],
  other: ['neutral', 'Autre'],
};

// AMF signals of a Paris-listed share: published net short positions and
// executives' dealings of the last six months (function only). Hidden for
// instruments the AMF does not cover or when the call fails.
export const AMFCard = ({ symbol, name, className }) => {
  const [signal, setSignal] = useState(null);

  useEffect(() => {
    if (!symbol || !name) return undefined;
    let alive = true;
    api.get('/market/amf', { params: { symbol, name } })
      .then((res) => { if (alive && res?.data?.eligible) setSignal(res.data.signal); })
      .catch(() => {});
    return () => { alive = false; };
  }, [symbol, name]);

  if (!signal) return null;
  const { short, deals } = signal;
  const market = deals.filter((d) => d.kind !== 'other');
  return (
    <Card className={className} data-testid="amf-card">
      <CardHeader icon={Landmark} title="Signaux AMF" description="Données publiques du régulateur : positions courtes publiées et opérations des dirigeants sur six mois." />
      <div className="space-y-5">
        <div>
          <p className="eyebrow mb-2">Positions courtes</p>
          {short ? (
            <>
              <p className="text-sm text-fg-body">
                <span className="num font-medium text-fg">{formatPercent(short.total_ratio, { sign: false, decimals: 2 })}</span> du capital parié à la baisse par {short.holders.length} fonds (au {formatDate(short.as_of, 'long')}).
              </p>
              <ul className="mt-2 space-y-1 text-xs text-fg-subtle">
                {short.holders.slice(0, 4).map((h) => <li key={h.holder} className="flex justify-between gap-3"><span className="truncate">{h.holder}</span><span className="num">{formatPercent(h.ratio, { sign: false, decimals: 2 })}</span></li>)}
              </ul>
            </>
          ) : (
            <p className="text-sm text-fg-muted">Aucune position courte publiée (seuil de 0,5 % du capital).</p>
          )}
        </div>
        <div>
          <p className="eyebrow mb-2">Dirigeants</p>
          {deals.length === 0 ? (
            <p className="text-sm text-fg-muted">Aucune déclaration sur six mois.</p>
          ) : (
            <>
              {market.length > 0 && (
                <p className="mb-2 text-sm text-fg-body">
                  Achats : <Money value={signal.bought} decimals={0} sensitive={false} className="text-gain-300" /> · ventes : <Money value={signal.sold} decimals={0} sensitive={false} className="text-loss-300" />
                </p>
              )}
              <ul className="divide-y divide-line">
                {deals.slice(0, 6).map((d) => {
                  const [tone, label] = KIND[d.kind] || KIND.other;
                  return (
                    <li key={d.numero} className="flex items-start gap-3 py-2 text-sm first:pt-0 last:pb-0" data-testid="amf-deal">
                      <Badge tone={tone} className="mt-0.5">{label}</Badge>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-fg">{d.function}</p>
                        <p className="text-xs text-fg-subtle">
                          {formatDate(d.date)} · {d.nature}
                          {d.amount ? <> · <Money value={d.amount} decimals={0} sensitive={false} /></> : d.volume ? ` · ${formatNumber(d.volume)} titres` : ''}
                        </p>
                      </div>
                      {d.url && (
                        <a href={d.url} target="_blank" rel="noreferrer noopener" className="mt-0.5 text-fg-subtle hover:text-gold-300" aria-label={`Déclaration ${d.numero} sur le site de l’AMF`}>
                          <ExternalLink size={14} aria-hidden="true" />
                        </a>
                      )}
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>
        <p className="text-xs leading-relaxed text-fg-subtle">Une position courte est un pari de fonds sur une baisse, pas une information sur l’entreprise. Les attributions d’actions gratuites et les nantissements ne sont pas des achats sur le marché.</p>
      </div>
    </Card>
  );
};

export default AMFCard;
